import GlobalEvent, { useGlobalState } from 'js-events-listener/react';
import { useEffect } from 'react';
import RequestBE from './RequestBE.utils';
import Persist, { PersistReady } from './Persist.utils';
import { BE_HOST } from './host';
import { VarHelper } from 'helpers';

interface ISetters {
  setSelectedCasks?: (v: any) => void;
  setEsignRequests?: (v: any) => void;
  setIsModalVisible?: (v: boolean) => void;
  [additionSetter: string]: (v: any) => void;
}

class EsignStore extends PersistReady {
  constructor() {
    super();
  }

  state: any = {
    selectedCasks: [],
    esignRequests: [],
    isModalVisible: false,
  };

  setters: ISetters = {};

  updateState(obj, allowUndefined = true) {
    for (let key in obj) {
      if (allowUndefined || (obj[key] !== null && obj[key] !== undefined))
        this.state[key] = obj[key];
    }
  }

  createStore() {
    const [selectedCasks, setSelectedCasks] = useGlobalState<Array<any>>(
      this.state.selectedCasks,
      'esignStore_selectedCasks'
    );
    const [esignRequests, setEsignRequests] = useGlobalState<Array<any>>(
      this.state.esignRequests,
      'esignStore_esignRequests'
    );
    const [isModalVisible, setIsModalVisible] = useGlobalState<boolean>(
      this.state.isModalVisible,
      'esignStore_isModalVisible'
    );

    if (!this.setters.setSelectedCasks)
      this.setters.setSelectedCasks = setSelectedCasks;
    if (!this.setters.setEsignRequests)
      this.setters.setEsignRequests = setEsignRequests;
    if (!this.setters.setIsModalVisible)
      this.setters.setIsModalVisible = setIsModalVisible;

    useEffect(() => {
      this.updateState({
        selectedCasks,
        esignRequests,
        isModalVisible,
      });
    }, [selectedCasks, esignRequests, isModalVisible]);

    return [
      {
        selectedCasks,
        esignRequests,
        isModalVisible,
      },
      {
        setSelectedCasks,
        setEsignRequests,
        setIsModalVisible,
        toggleCaskSelection: this.toggleCaskSelection,
        selectAllCasks: this.selectAllCasks,
        clearSelection: this.clearSelection,
        openModal: this.openModal,
        closeModal: this.closeModal,
        generatePdfPreview: this.generatePdfPreview,
        saveDraft: this.saveDraft,
        sendSignatureRequest: this.sendSignatureRequest,
        getEsignRequests: this.getEsignRequests,
        getMyEsignRequests: this.getMyEsignRequests,
        getSignedDocuments: this.getSignedDocuments,
        sendReminder: this.sendReminder,
        deleteDraft: this.deleteDraft,
        shareDocument: this.shareDocument,
      },
    ];
  }

  toggleCaskSelection = (cask: any) => {
    const currentSelection = this.state.selectedCasks;
    // Use 'id' field as primary identifier (required by backend)
    const caskId = cask.id;
    
    if (!caskId) {
      console.error('Cask missing id field:', cask);
      return;
    }
    
    const index = currentSelection.findIndex((item) => item.id === caskId);

    if (index > -1) {
      // Remove from selection
      const newSelection = [
        ...currentSelection.slice(0, index),
        ...currentSelection.slice(index + 1),
      ];
      if (this.setters.setSelectedCasks) {
        this.setters.setSelectedCasks(newSelection);
      }
    } else {
      // Add to selection
      if (this.setters.setSelectedCasks) {
        this.setters.setSelectedCasks([...currentSelection, cask]);
      }
    }
  };

  selectAllCasks = (casks: Array<any>, selectAll: boolean) => {
    if (this.setters.setSelectedCasks) {
      this.setters.setSelectedCasks(selectAll ? casks : []);
    }
  };

  clearSelection = () => {
    if (this.setters.setSelectedCasks) {
      this.setters.setSelectedCasks([]);
    }
  };

  openModal = () => {
    if (this.setters.setIsModalVisible) {
      this.setters.setIsModalVisible(true);
    }
  };

  closeModal = () => {
    if (this.setters.setIsModalVisible) {
      this.setters.setIsModalVisible(false);
    }
  };

  generatePdfPreview = async (data: {
    caskIds: string[];
    clientEmail: string;
    clientName: string;
  }) => {
    return VarHelper.erria(async () => {
      const res = await RequestBE.post(
        BE_HOST + '/esign/generate-pdf-preview',
        data
      );
      return res.data;
    });
  };

  saveDraft = async (data: {
    caskIds: string[];
    clientEmail: string;
    clientName: string;
    pdfPath?: string;
  }) => {
    return VarHelper.erria(async () => {
      const res = await RequestBE.post(
        BE_HOST + '/esign/save-draft',
        data
      );
      if (res.data && res.data.success) {
        // Clear selection after successful save
        this.clearSelection();
        this.closeModal();
      }
      return res.data;
    });
  };

  deleteDraft = async (id: number) => {
    return VarHelper.erria(async () => {
      const res = await RequestBE.post(BE_HOST + '/esign/delete-draft', { id });
      if (res && res.data) {
        return res.data;
      }
      return res;
    });
  };

  shareDocument = async (data: { esignId: number; recipientEmails: string[] }) => {
    return VarHelper.erria(async () => {
      const res = await RequestBE.post(BE_HOST + '/esign/share-document', data);
      if (res && res.data) {
        return res.data;
      }
      return res;
    });
  };

  sendSignatureRequest = async (data: {
    caskIds: string[];
    clientEmail: string;
    clientName: string;
  }) => {
    return VarHelper.erria(async () => {
      const res = await RequestBE.post(
        BE_HOST + '/esign/create-signature-request',
        data
      );
      if (res.data && res.data.success) {
        // Clear selection after successful send
        this.clearSelection();
        this.closeModal();
      }
      return res.data;
    });
  };

  getEsignRequests = async (params?: { status?: string; limit?: number; offset?: number }) => {
    return VarHelper.erria(async () => {
      // Add timestamp to prevent caching
      const paramsWithTimestamp = { ...(params || {}), _t: Date.now() };
      const res = await RequestBE.get(BE_HOST + '/esign/list', paramsWithTimestamp);
      if (res.data && res.data.success && Array.isArray(res.data.data)) {
        if (this.setters.setEsignRequests) {
          this.setters.setEsignRequests(res.data.data);
        }
        return res.data;
      }
      return res.data;
    });
  };

  getMyEsignRequests = async (params?: { status?: string; limit?: number; offset?: number }) => {
    return VarHelper.erria(async () => {
      // Add timestamp to prevent caching
      const paramsWithTimestamp = { ...(params || {}), _t: Date.now() };
      const res = await RequestBE.get(BE_HOST + '/esign/my-list', paramsWithTimestamp);
      if (res.data && res.data.success && Array.isArray(res.data.data)) {
        if (this.setters.setEsignRequests) {
          this.setters.setEsignRequests(res.data.data);
        }
        return res.data;
      }
      return res.data;
    });
  };

  getSignedDocuments = async (params?: { limit?: number; offset?: number }) => {
    return VarHelper.erria(async () => {
      const res = await RequestBE.get(BE_HOST + '/esign/list', {
        status: 'signed',
        ...params,
      });
      return res.data;
    });
  };

  sendReminder = async (signatureRequestId: string) => {
    return VarHelper.erria(async () => {
      const res = await RequestBE.post(BE_HOST + '/esign/send-reminder', {
        signatureRequestId,
      });
      return res.data;
    });
  };
}

export default new EsignStore();

